<script setup lang="ts">
import type { Product as CatalogProduct } from '~/types'
import type { Product as SearchProduct } from '~/server/api/search'
import type { CampaignProductItem } from '~/composables/useCampaignProductGroups'
import IconHeart from '~icons/mdi/heart'
import IconHeartOutline from '~icons/mdi/heart-outline'

interface Props {
  btnClass?: string
  imgSrc?: string
  imgClass?: string
  aspectRatio?: string
  alt: string
  isFavourite?: boolean
  minHeight?: string
  maxHeight?: string
  maxWidth?: string
  showFavoriteBtn?: boolean
  fallbackSrc?: string
  navigationPath?: string
  product?: CatalogProduct | SearchProduct | CampaignProductItem
  currentPrice?: string
  scalePricing?: string
  campaign?: {
    beforePrice: string | number
    afterPrice: string | number
    discount: number
    discount_type: string
    discountPercent?: number
  }
  temperatureType?: 'CHILLED' | 'FREEZ' | null
  discountFlagPercent?: number
}

const props = withDefaults(defineProps<Props>(), {
  isFavourite: () => false,
  btnClass: () => 'vmz-product-image__btn',
  imgSrc: () => '',
  imgClass: String,
  aspectRatio: () => '4/3',
  showFavoriteBtn: () => true,
})

const isAlternativeProduct = inject<ComputedRef<boolean>>('isAlternativeSheet', computed(() => false))

const emit = defineEmits<{
  (e: 'update:isFavourite', value: boolean): void
  (e: 'saved', payload: { listName: string, isNewList: boolean, quantity: number }): void
}>()

const hasError = ref(false)
const imageUrl = computed(() => hasError.value ? props.fallbackSrc : props.imgSrc || props.fallbackSrc)
const isFavoriteDialogOpen = ref(false)

const localePath = useLocalePath()
const isFavouriteProduct = computed({
  get: () => props.isFavourite,
  set: (value: boolean) => emit('update:isFavourite', value),
})

function toggleLike() {
  if (isFavouriteProduct.value) {
    // If already favorite, just unfavorite
    isFavouriteProduct.value = false
  }
  else {
    // If not favorite, show dialog
    isFavoriteDialogOpen.value = true
  }
}

function confirmAddToFavorites() {
  isFavouriteProduct.value = true
}

/*
  when different variant is selected,
  the image is changed, so we need to reset the error state
 */
watch(() => props.imgSrc, () => {
  hasError.value = false
})

const campaignDiscount = computed(() => props.campaign?.discount ?? props.campaign?.discountPercent)

const showCampaignFlag = computed(() =>
  !!campaignDiscount.value
  && (
    (props.scalePricing !== 'INDIVIDUAL' && props.scalePricing !== 'GPO')
    || (!!props.currentPrice && !!props.campaign?.afterPrice && Number(props.campaign.afterPrice) < Number(props.currentPrice))
  ),
)

// Campaign flag takes precedence (with its GPO/scale-pricing condition).
// Falls back to the static discount flag which is unconditional (visible to all users).
const flagPercent = computed(() =>
  showCampaignFlag.value ? campaignDiscount.value : props.discountFlagPercent,
)
</script>

<template>
  <div class="vmz-product-image">
    <div
      v-if="props.temperatureType === 'CHILLED' || props.temperatureType === 'FREEZ'"
      class="d-flex justify-end mb-1"
    >
      <VmzTempratureIcons :temperature-type="props.temperatureType" />
    </div>
    <!-- Todo: add shop::display-product-details feature flag -->
    <NuxtLink
      v-if="navigationPath && navigationPath?.length > 0"
      :to="localePath(navigationPath)"
      data-nav-id="product_image"
      :target="isAlternativeProduct ? '_blank' : undefined"
    >
      <vmz-img
        :src="imageUrl"
        :aspect-ratio="props.aspectRatio"
        :class="props.imgClass"
        class="rounded-lg vmz-product-image__img"
        :max-height="maxHeight"
        :max-width="maxWidth"
        :min-height="minHeight"
        :alt="props.alt"
        data-test-id="product-image"
        @error="hasError = true"
      />
    </NuxtLink>

    <vmz-img
      v-else
      :src="imageUrl"
      :aspect-ratio="props.aspectRatio"
      :class="props.imgClass"
      class="rounded-lg vmz-product-image__img"
      :max-height="maxHeight"
      :max-width="maxWidth"
      :min-height="minHeight"
      :alt="props.alt"
      data-test-id="product-image"
      data-nav-id="product_image"
      @error="hasError = true"
    />
    <vmz-btn
      v-if="showFavoriteBtn"
      color="red-lighten-2"
      :icon="isFavouriteProduct ? IconHeart : IconHeartOutline"
      variant="text"
      :class="btnClass"
      data-test-id="product-image-favorite-button"
      data-track-id="product_toggle_favorite"
      @click="toggleLike"
    />
    <VmzDiscountFlag v-if="flagPercent" :percent="flagPercent" />

    <FavoriteConfirmDialog
      v-model:is-open="isFavoriteDialogOpen"
      :products="product"
      :current-price="currentPrice"
      @confirm="confirmAddToFavorites"
      @cancel="() => {}"
      @saved="(payload) => emit('saved', payload)"
    />
  </div>
</template>

<style scoped lang="scss">
  .vmz-product-image {
    height: auto;
    position: relative;

    &__btn {
      position: absolute;
      top: 0;
    }
    &__img {
      height: 100%;
    }
  }
</style>
